import React, { useState } from "react";
import {
    Avatar,
    Button,
    TextField,
    Typography,
    Box,
    Alert,
    InputAdornment,
    CircularProgress,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import DirectionsCarFilledIcon from "@mui/icons-material/DirectionsCarFilled";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { login } from "../services/authService";
import SwiftaLogo from "../assets/images/logo.png";

// ---------- COLORS ----------
const COLORS = {
    gradientBg: "linear-gradient(120deg, #5c5a5a, #4ab7bd, #2b2b2b)",
    gradientBtn: "linear-gradient(90deg, #1ab98a, #32dcc7)",
    gradientBtnHover: "linear-gradient(90deg, #38f9d7, #43e97b)",
    primary: "#1ab98a",
    secondary: "#32dcc7",
    brandText: "#fff",
    cardBg: "rgba(255,255,255,0.12)",
    cardShadow: "0 12px 50px rgba(0,0,0,0.14)",
    iconShadow: "0px 4px 20px rgba(0,0,0,0.4)",
    inputBg: "rgba(255,255,255,0.21)",
    inputText: "#fff",
    label: "#f0fff9",
    subtitle: "#d1fff9",
    iconBg: "#efefef",
    iconCar: "#1ab98a",
    alertBg: "#191c24",
    copyright: "#e5fff8",
    copyrightAccent: "#ffffff",
    lockBg: "#1ab98a"
};
// ---------- END COLORS ----------

// CSS in JS for gradient background
const bgGradient = {
    background: COLORS.gradientBg,
    backgroundSize: "400% 400%",
    animation: "gradientShift 15s ease infinite",
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
};

// Glass card, now less wide for desktop look
const glassCard = {
    backdropFilter: "blur(20px)",
    background: COLORS.cardBg,
    borderRadius: "22px",
    padding: "40px 32px",
    width: "100%",
    maxWidth: "420px", // less wide; adjust this value for your design taste
    boxShadow: COLORS.cardShadow,
    margin: "90px 18px 32px", // top margin for breathing room
};

// Brand text
const brandStyle = {
    fontWeight: 900,
    letterSpacing: 1.2,
    fontSize: "32px",
    color: COLORS.brandText,
    textShadow: COLORS.iconShadow,
};

const LoginPage: React.FC = () => {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setLoading(true);
        try {
            const token = await login(username, password);
            localStorage.setItem("jwtToken", token);
            navigate("/");
        } catch {
            setError("Invalid username or password.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={bgGradient}>
            {/* Brand/Header */}
            <motion.div
                initial={{ y: -120, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 1.2 }}
                style={{
                    position: "absolute",
                    top: 50,
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    zIndex: 99,
                }}
            >
                <Box
                    component="img"
                    src={SwiftaLogo}
                    alt="SwiftaGo"
                    sx={{
                        width: 65,
                        height: 65,
                        filter: "drop-shadow(0px 5px 25px rgba(0,0,0,0.13))",
                        mr: 1,
                    }}
                />
                <Typography component="span" sx={brandStyle}>
                    SwiftaGo Admin
                </Typography>
            </motion.div>

            {/* Login Glass Panel */}
            <motion.div
                initial={{ y: 120, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 1 }}
                style={glassCard}
            >
                <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
                    <Avatar
                        sx={{
                            mb: 2,
                            bgcolor: COLORS.lockBg,
                            width: 62,
                            height: 62,
                        }}
                    >
                        <LockOutlinedIcon fontSize="large" />
                    </Avatar>
                    <Typography component="h1" variant="h4" sx={{ fontWeight: 700, color: COLORS.brandText }}>
                        Admin Login
                    </Typography>
                    <Typography variant="subtitle2" sx={{ mt: 1, color: COLORS.subtitle }}>
                        Secure portal for ride hailing operations
                    </Typography>
                </Box>

                <form onSubmit={handleLogin} style={{ width: "100%" }}>
                    <TextField
                        margin="normal"
                        fullWidth
                        label="Username"
                        variant="outlined"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        disabled={loading}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Avatar
                                        sx={{
                                            bgcolor: COLORS.iconBg,
                                            width: 28,
                                            height: 28,
                                        }}
                                    >
                                        <DirectionsCarFilledIcon sx={{ color: COLORS.iconCar }} />
                                    </Avatar>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            input: { color: COLORS.inputText },
                            "& .MuiOutlinedInput-root": {
                                background: COLORS.inputBg,
                                borderRadius: 2,
                            },
                            label: { color: COLORS.label },
                        }}
                    />
                    <TextField
                        margin="normal"
                        fullWidth
                        name="password"
                        label="Password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={loading}
                        variant="outlined"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LockOutlinedIcon sx={{ color: COLORS.iconCar }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            input: { color: COLORS.inputText },
                            "& .MuiOutlinedInput-root": {
                                background: COLORS.inputBg,
                                borderRadius: 2,
                            },
                            label: { color: COLORS.label },
                        }}
                    />
                    <Button
                        fullWidth
                        variant="contained"
                        type="submit"
                        disabled={loading}
                        sx={{
                            mt: 3,
                            py: 1.2,
                            fontWeight: 700,
                            fontSize: 16,
                            borderRadius: 2,
                            transition: "0.3s",
                            background: COLORS.gradientBtn,
                            boxShadow: "0 8px 20px rgba(50,220,199,0.19)",
                            "&:hover": {
                                background: COLORS.gradientBtnHover,
                                transform: "translateY(-2px)",
                                boxShadow: "0 12px 24px rgba(50,220,199,0.26)",
                            },
                        }}
                    >
                        {loading ? <CircularProgress size={22} color="inherit" /> : "LOGIN"}
                    </Button>
                    {error && (
                        <Alert severity="error" sx={{ mt: 2, borderRadius: 2, background: COLORS.alertBg, color: "#fff" }}>
                            {error}
                        </Alert>
                    )}
                </form>

                <Typography
                    variant="caption"
                    sx={{
                        mt: 3,
                        display: "block",
                        color: COLORS.copyright,
                        textAlign: "center",
                    }}
                >
                    &copy; {new Date().getFullYear()} SwiftaGo. All rights reserved. <br />
                    <span style={{ color: COLORS.copyrightAccent }}>Enterprise-grade security.</span>
                </Typography>
            </motion.div>

            {/* CSS Gradient Animation (for backgrounds) */}
            <style>{`
                @keyframes gradientShift {
                  0% { background-position: 0% 50%; }
                  50% { background-position: 100% 50%; }
                  100% { background-position: 0% 50%; }
                }
            `}</style>
        </Box>
    );
};

export default LoginPage;
